import { format, isValid, parseISO } from 'date-fns'

import { Job } from '../../../api/jobs'
import {
  ProgressAiBatch,
  ReviewQueuePoint,
  ReviewTask,
  ReviewTaskPointFilter,
  ReviewTaskPointSequence,
  ReviewTaskStatus,
} from '../../../api/reviewTasks'

export interface ReviewFloorStat {
  floor_code: string
  name: string
  visited: number
  total: number
  percent: number
}

export const isAiBatchRunning = (batch?: ProgressAiBatch | null) =>
  batch?.status === 'running' || batch?.status === 'not_started'

export const isAiBatchFinished = (batch?: ProgressAiBatch | null) => {
  if (!batch) return false
  return (
    batch.status === 'succeeded' ||
    batch.status === 'failed' ||
    batch.status === 'cancelled'
  )
}

export const formatUsdAmount = (
  value: number | null | undefined,
  digits = 1,
) => `$${(Number(value) || 0).toFixed(digits)}`

export const formatAiBatchEta = (seconds: number | null | undefined) => {
  if (seconds == null || seconds < 0) return null
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) return `${hours}h:${String(minutes).padStart(2, '0')}min`
  return `${minutes}min`
}

export const aiBatchFinishedCount = (batch: ProgressAiBatch) =>
  (Number(batch.points_done) || 0) +
  (Number(batch.points_failed) || 0) +
  (Number(batch.points_skipped) || 0)

export const formatAiBatchStatus = (
  batch: ProgressAiBatch | null | undefined,
) => {
  if (!batch) return 'no batch'
  const finished = aiBatchFinishedCount(batch)
  const total = Number(batch.points_total) || 0
  const cost = formatUsdAmount(batch.cost_usd, 1)
  if (batch.status === 'not_started') return `${finished}/${total} not started`
  if (batch.status === 'running') {
    const eta = formatAiBatchEta(batch.eta_seconds)
    return eta
      ? `${finished}/${total} processed · eta ${eta} · ${cost}`
      : `${finished}/${total} processed · ${cost}`
  }
  if (batch.status === 'succeeded') {
    return `${finished}/${total} done ${cost}`
  }
  if (batch.status === 'cancelled') {
    return `${finished}/${total} cancelled ${cost}`
  }
  return `${finished}/${total} failed ${cost}`
}

export const formatReviewTaskProgress = (task: ReviewTask) => {
  const batch = task.ai_batch
  if (batch && isAiBatchRunning(batch)) {
    const finished = aiBatchFinishedCount(batch)
    const total = Number(batch.points_total) || 0
    return `${finished}/${total} processed`
  }
  if (task.ai_assist) {
    return `${task.suggestion_stops_viewed || 0} / ${
      task.suggestion_stops_total || 0
    }`
  }
  return `${task.points_viewed} / ${task.points_total}`
}

export const floorNameFromProject = (
  floors: Array<{ floor_code: string; name?: string }> | undefined,
  floorCode: string,
) => {
  return (
    floors?.find(floor => floor.floor_code === floorCode)?.name || floorCode
  )
}

export const firstRemainingFloorCode = (queue: ReviewQueuePoint[]) => {
  return firstRemainingQueuePoint(queue)?.floor_code
}

export const firstRemainingQueuePoint = (queue: ReviewQueuePoint[]) => {
  return queue.find(entry => !entry.visited_action) || queue[0] || null
}

export const isReviewQueueFinished = (queue: ReviewQueuePoint[]) => {
  return queue.length === 0 || queue.every(entry => Boolean(entry.visited_action))
}

/** Trackers as returned on review queue meta. */
export type ReviewTrackerRef = { id: number; name: string }

/** One AI-assist navigation stop: a queue point + tracker that has suggestions. */
export type AiSuggestionNavStop = {
  queueIndex: number
  viewpointPk: number
  pointId: number | string
  trackerId: number
  trackerName: string
}

type AiSuggestionResultLike = {
  viewpoints_point_id: number
  status: string
  suggestions?: Array<{ tracker?: string | null }> | null
}

/** Viewpoint PKs that have at least one succeeded AI suggestion. */
export const suggestionViewpointPkSet = (
  results: AiSuggestionResultLike[],
): Set<number> => {
  const set = new Set<number>()
  for (const result of results) {
    if (
      result.status === 'succeeded' &&
      Array.isArray(result.suggestions) &&
      result.suggestions.length > 0
    ) {
      set.add(result.viewpoints_point_id)
    }
  }
  return set
}

/**
 * Ordered stops for AI-assist Next/Prev: queue order, then tracker meta order
 * for trackers that appear in that point's suggestions.
 */
export const buildAiSuggestionNavStops = (
  queue: ReviewQueuePoint[],
  trackers: ReviewTrackerRef[],
  results: AiSuggestionResultLike[],
): AiSuggestionNavStop[] => {
  const byViewpoint = new Map<number, AiSuggestionResultLike>()
  for (const result of results) {
    if (
      result.status === 'succeeded' &&
      Array.isArray(result.suggestions) &&
      result.suggestions.length > 0
    ) {
      byViewpoint.set(result.viewpoints_point_id, result)
    }
  }
  const stops: AiSuggestionNavStop[] = []
  queue.forEach((entry, queueIndex) => {
    const result = byViewpoint.get(entry.viewpoint_pk)
    if (!result?.suggestions?.length) {
      return
    }
    const names = new Set(
      result.suggestions
        .map(suggestion => (suggestion.tracker || '').trim().toLowerCase())
        .filter(Boolean),
    )
    for (const tracker of trackers) {
      if (names.has(tracker.name.trim().toLowerCase())) {
        stops.push({
          queueIndex,
          viewpointPk: entry.viewpoint_pk,
          pointId: entry.point_id,
          trackerId: tracker.id,
          trackerName: tracker.name,
        })
      }
    }
  })
  return stops
}

/**
 * Index of the current stop in `stops`, or the last stop at/before the current
 * (queueIndex, trackerId) position when the selection is not itself a stop.
 */
export const findAiSuggestionStopIndex = (
  stops: AiSuggestionNavStop[],
  queueIndex: number,
  trackerId: number | null,
  trackerOrder: number[],
): number => {
  const exact = stops.findIndex(
    stop => stop.queueIndex === queueIndex && stop.trackerId === trackerId,
  )
  if (exact >= 0) {
    return exact
  }
  const curOrder = trackerId == null ? -1 : trackerOrder.indexOf(trackerId)
  let lastPassed = -1
  for (let i = 0; i < stops.length; i += 1) {
    const stop = stops[i]
    if (stop.queueIndex < queueIndex) {
      lastPassed = i
      continue
    }
    if (stop.queueIndex > queueIndex) {
      break
    }
    const stopOrder = trackerOrder.indexOf(stop.trackerId)
    if (curOrder < 0 || stopOrder <= curOrder) {
      lastPassed = i
    }
  }
  return lastPassed
}

export const filterQueueToSuggestionPoints = (
  queue: ReviewQueuePoint[],
  suggestionViewpointPks: Set<number>,
): ReviewQueuePoint[] => {
  if (suggestionViewpointPks.size === 0) {
    return []
  }
  return queue.filter(entry => suggestionViewpointPks.has(entry.viewpoint_pk))
}

export const isPendingReviewStatus = (status?: ReviewTaskStatus | string) => {
  return status === 'not_started' || status === 'in_progress'
}

export const withPendingReviewTaskCount = <
  T extends { pending_review_task_count?: number },
>(
  projectData: T,
  count: number,
): T => {
  return {
    ...projectData,
    pending_review_task_count: Math.max(0, Number(count) || 0),
  }
}

export const reviewFloorStats = (
  queue: ReviewQueuePoint[],
  floors?: Array<{ floor_code: string; name?: string }>,
): ReviewFloorStat[] => {
  const byCode = new Map<string, { visited: number; total: number }>()
  queue.forEach(entry => {
    const current = byCode.get(entry.floor_code) || { visited: 0, total: 0 }
    current.total += 1
    if (entry.visited_action) {
      current.visited += 1
    }
    byCode.set(entry.floor_code, current)
  })

  const orderedCodes: string[] = []
  const seen = new Set<string>()
  ;[...(floors || [])].reverse().forEach(floor => {
    if (byCode.has(floor.floor_code) && !seen.has(floor.floor_code)) {
      orderedCodes.push(floor.floor_code)
      seen.add(floor.floor_code)
    }
  })
  byCode.forEach((_value, code) => {
    if (!seen.has(code)) {
      orderedCodes.push(code)
    }
  })

  return orderedCodes.map(floor_code => {
    const stats = byCode.get(floor_code) || { visited: 0, total: 0 }
    return {
      floor_code,
      name: floorNameFromProject(floors, floor_code),
      visited: stats.visited,
      total: stats.total,
      percent: stats.total
        ? Math.round((stats.visited / stats.total) * 100)
        : 0,
    }
  })
}

export const formatCaptureDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value.includes('T') ? value : `${value}T12:00:00`)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export const isPendingReviewJob = (job: Job) => {
  if (job.type?.is_active === false) {
    return false
  }
  const completed = Number(job.completed_units) || 0
  const total = Number(job.total_units)
  if (Number.isFinite(total) && total > 0) {
    return completed < total
  }
  return completed <= 0
}

export const STATUS_FILTERS: { id: ReviewTaskStatus | 'all'; label: string }[] =
  [
    { id: 'all', label: 'All' },
    { id: 'not_started', label: 'Not Started' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'completed', label: 'Completed' },
  ]

export const POINT_FILTER_OPTIONS: {
  id: ReviewTaskPointFilter
  label: string
}[] = [
  { id: 'none', label: 'none' },
  { id: 'past_7_days', label: 'past 7 days' },
]

export const POINT_SEQUENCE_OPTIONS: {
  id: ReviewTaskPointSequence
  label: string
}[] = [
  { id: 'id', label: 'id' },
  { id: 'robot', label: 'robot' },
]

/** Gaps longer than this are pauses and are excluded from total review time. */
export const REVIEW_ACTIVITY_PAUSE_MS = 10 * 60 * 1000

export const formatActivityGap = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const ss = String(seconds).padStart(2, '0')
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`
  }
  return `${minutes}:${ss}`
}

export const formatDurationHoursMinutes = (seconds: number) => {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  return `${hours}h ${minutes}m`
}

export const formatMinutesPerPoint = (seconds: number, viewed: number) => {
  if (!viewed) {
    return '—'
  }
  return `${(seconds / 60 / viewed).toFixed(1)} min/point`
}

export const formatViewpointNavLabel = (
  entry?: { point_id?: number | string | null } | null,
  suggestionOrdinal?: number,
) => {
  if (!entry || entry.point_id == null) {
    return ''
  }
  const base = String(entry.point_id)
  if (!suggestionOrdinal || suggestionOrdinal < 1) {
    return base
  }
  return `${base} - ${suggestionOrdinal}`
}

export const suggestionStopOrdinal = (
  stops: AiSuggestionNavStop[],
  stop: AiSuggestionNavStop | null | undefined,
) => {
  if (!stop) {
    return 0
  }
  const samePoint = stops.filter(
    candidate => String(candidate.pointId) === String(stop.pointId),
  )
  const index = samePoint.findIndex(
    candidate =>
      candidate.queueIndex === stop.queueIndex &&
      candidate.trackerId === stop.trackerId,
  )
  return index >= 0 ? index + 1 : 0
}

export const formatAiSuggestionNavLabel = (
  stop: AiSuggestionNavStop | null | undefined,
  stops: AiSuggestionNavStop[],
) => {
  if (!stop) {
    return ''
  }
  return formatViewpointNavLabel(
    { point_id: stop.pointId },
    suggestionStopOrdinal(stops, stop),
  )
}

export const aiSuggestionStopProgress = (
  stops: AiSuggestionNavStop[],
  queue: ReviewQueuePoint[],
) => {
  const total = stops.length
  const viewed = stops.filter(stop =>
    Boolean(queue[stop.queueIndex]?.visited_action),
  ).length
  return { viewed, total }
}

export const elapsedReviewSeconds = (
  startedAt?: string | null,
  completedAt?: string | null,
  nowMs = Date.now(),
) => {
  if (!startedAt) {
    return 0
  }
  const start = parseISO(startedAt)
  if (!isValid(start)) {
    return 0
  }
  const end = completedAt ? parseISO(completedAt) : new Date(nowMs)
  const endMs = isValid(end) ? end.getTime() : nowMs
  return Math.max(0, (endMs - start.getTime()) / 1000)
}

export const formatTaskDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }
  const parsed = parseISO(value)
  if (!isValid(parsed)) {
    return '—'
  }
  return format(parsed, 'MMM d, h:mm a')
}

export const formatCompactTaskDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }
  const parsed = parseISO(value)
  if (!isValid(parsed)) {
    return '—'
  }
  return format(parsed, 'MMM d, h:mm a')
}

export const pointFilterLabel = (value: ReviewTaskPointFilter) => {
  return POINT_FILTER_OPTIONS.find(option => option.id === value)?.label ?? value
}

export const pointSequenceLabel = (value: ReviewTaskPointSequence) => {
  return (
    POINT_SEQUENCE_OPTIONS.find(option => option.id === value)?.label ?? value
  )
}
